/**
 * IMPORTS
 */

import { useNavigate } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { Undo2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// components
import { Button } from "@components/button/button";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";
import { Tabs, TabsList, TabsTrigger } from "@components/tabs";

// hooks
import { useAtualizarUsuario } from "@/hooks/usuários/atualizar-cliente";

// store-zustand
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

// utils / sleep
import { sleep } from "@/utils/sleep/sleep";

// schema
import { schemaUserChangePassword } from "./schema/user-schema";

type FormData = z.infer<typeof schemaUserChangePassword>;

const MyDataPages = () => {
  const navigate = useNavigate();
  const { user, setUser } = useStoreZustandUserAuth();

  const { atualizarUsuarioAsync, isLoading } = useAtualizarUsuario();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schemaUserChangePassword),
    defaultValues: {
      nome: user?.nome ?? "",
      email: user?.email ?? "",
      changePassword: false,
      password: "",
      confirmPassword: "",
    },
  });

  const changePassword = watch("changePassword");

  const onSubmit = async (data: FormData) => {
    // monta o payload base
    const payload: any = {
      nome: data.nome,
      email: data.email,
    };

    // adiciona a senha somente se confirmPassword estiver preenchida
    if (data.confirmPassword && data.confirmPassword.trim() !== "") {
      payload.senha = data.confirmPassword;
    }

    // chamada da função de atualização
    await atualizarUsuarioAsync({
      user_id: Number(user?.id),
      payload,
    });

    const dataUpdate = {
      ...user,
      nome: data.nome,
      email: data.email,
    } as FormData;

    setUser(dataUpdate);

    await sleep(1000);
    window.location.reload();
  };

  return (
    <Tabs defaultValue="all">
      <div className="flex items-center">
        <TabsList>
          <TabsTrigger value="all">Meus dados</TabsTrigger>
        </TabsList>
        <div className="mb-4 ml-auto mr-4 flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            className="h-8 gap-1 text-neutral-50"
            onClick={() => navigate("/")}
          >
            <Undo2 className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Voltar</span>
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="rounded-lg bg-white p-6 shadow-md">
        <h2 className="text-xl font-semibold text-gray-800">Configurações da Conta</h2>
        <p className="mb-6 text-sm text-gray-500">Atualize suas informações pessoais.</p>

        {/* Nome */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-zinc-800">Nome completo</label>
          <input
            {...register("nome")}
            className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
            placeholder="Digite seu nome"
          />
          {errors.nome && <p className="mt-1 text-sm text-red-500">{errors.nome.message}</p>}
        </div>

        {/* Email */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-zinc-800">E-mail</label>
          <input
            {...register("email")}
            className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
            placeholder="Digite seu e-mail"
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
        </div>

        {/* Alterar senha */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-zinc-800">Alterar a senha</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={changePassword}
                onChange={() => setValue("changePassword", true)}
              />
              <span className="ml-2 text-gray-700">Sim</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={!changePassword}
                onChange={() => setValue("changePassword", false)}
              />
              <span className="ml-2 text-gray-700">Não</span>
            </label>
          </div>
        </div>

        {/* Campos de senha */}
        {changePassword && (
          <>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-zinc-800">Senha</label>
              <input
                type="password"
                {...register("password")}
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                placeholder="Digite a nova senha"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-zinc-800">
                Confirmar senha
              </label>
              <input
                type="password"
                {...register("confirmPassword")}
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                placeholder="Confirme a nova senha"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.confirmPassword.message}</p>
              )}
            </div>
          </>
        )}

        <Button type="submit" className="mt-4 w-full rounded-lg bg-primary py-2 text-white">
          {isLoading ? <SpinnerSimple /> : "Alterar informações"}
        </Button>
      </form>
    </Tabs>
  );
};

/**
 * EXPORTS
 */
export { MyDataPages };
